import React, { useCallback, useState } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { DataTable } from "../../components/DataTable/DataTable";
import { useCollection } from "../../hooks/useCollection";
import {
  getSubjects,
  addSubject,
  updateSubject,
  deleteSubject,
  getSubjectTutorCount,
} from "../../services/dataService";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission, permissions } from "../../config/permissions";
import { logUserActivity } from "../../utils/activityLogger";
import { slugify, validateSubject, canMutateName } from "../../utils/subjectGuards";

const dialogFieldSx = {
  borderRadius: "8px",
  "& .MuiInputBase-root": { borderRadius: "6px" },
};

const actionButtonSx = { padding: "8px 32px", fontSize: 15, fontWeight: 500 };

const emptyForm = { id: "", name: "", order: 1, icon: "" };

// D6 write-time guard refusal — page-load tutorCount can go stale between
// load and confirm; state what's true now and that the table was refreshed.
const guardMessage = (count, verb) =>
  `${count} tutors now carry this name — can't ${verb} while that's true. The table has been refreshed.`;

const Subjects = () => {
  const { user } = useAuth();
  const userRole = user?.role || "";

  const fetchSubjects = useCallback(() => getSubjects(), []);
  const { data, loading, error, refetch } = useCollection(fetchSubjects);

  // Add/Edit dialog — one dialog, driven by dialogMode.
  const [dialogMode, setDialogMode] = useState(null); // null | "add" | "edit"
  const [editingRow, setEditingRow] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Delete confirm dialog.
  const [deleteRow, setDeleteRow] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "" });
  const showError = (message) => setSnackbar({ open: true, message });
  const closeSnackbar = () => setSnackbar({ open: false, message: "" });

  const auditActor = () => ({
    userId: user?.uid || user?.id || "unknown",
    userName: user?.name || user?.displayName || "Unknown User",
    userEmail: user?.email || "unknown@example.com",
  });

  const openAdd = () => {
    setEditingRow(null);
    setFormData(emptyForm);
    setFormErrors({});
    setDialogMode("add");
  };

  const openEdit = (row) => {
    setEditingRow(row);
    setFormData({
      id: row.id,
      name: row.name || "",
      order: row.order ?? 1,
      icon: row.icon || "",
    });
    setFormErrors({});
    setDialogMode("edit");
  };

  const closeDialog = () => {
    if (saving) return;
    setDialogMode(null);
  };

  const handleNameChange = (value) => {
    setFormData((prev) => ({
      ...prev,
      name: value,
      id: dialogMode === "add" ? slugify(value) : prev.id,
    }));
  };

  const handleSubmit = async () => {
    const errors = validateSubject({
      name: formData.name,
      order: formData.order,
    });
    if (
      dialogMode === "add" &&
      !errors.name &&
      data.some((s) => s.id === formData.id)
    ) {
      errors.name = "A subject with this name already exists";
    }
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    const { userId, userName, userEmail } = auditActor();
    const name = formData.name.trim();
    setSaving(true);
    try {
      if (dialogMode === "add") {
        const after = { name, order: formData.order, icon: formData.icon || "" };
        await addSubject({ id: formData.id, ...after });
        await logUserActivity({
          userId,
          userName,
          userEmail,
          action: "ADD_SUBJECT",
          entityType: "subject",
          entityId: formData.id,
          entityName: name,
          before: {},
          after,
          description: `${userName} added subject ${name}`,
        });
      } else {
        // D6 write-time re-check: only a rename can strand a tutor, and
        // only against the *old* name — order/icon-only edits strand no
        // one. tutorCount on the row is a page-load snapshot; rules don't
        // guard this (write is admin-only, no count check), so this read,
        // immediately before the write, is the actual guard.
        if (name !== editingRow.name) {
          const liveCount = await getSubjectTutorCount(editingRow.name);
          if (liveCount > 0) {
            showError(guardMessage(liveCount, "rename"));
            setDialogMode(null);
            await refetch();
            return;
          }
        }
        const before = {
          name: editingRow.name,
          order: editingRow.order,
          icon: editingRow.icon || "",
        };
        const after = { name, order: formData.order, icon: formData.icon || "" };
        await updateSubject(formData.id, after);
        await logUserActivity({
          userId,
          userName,
          userEmail,
          action: "UPDATE_SUBJECT",
          entityType: "subject",
          entityId: formData.id,
          entityName: name,
          before,
          after,
          description: `${userName} updated subject ${name}`,
        });
      }
      setDialogMode(null);
      await refetch();
    } catch (e) {
      showError(e.message || "Failed to save subject.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteRow) return;
    const { userId, userName, userEmail } = auditActor();
    setDeleting(true);
    try {
      // D6 write-time re-check — same reasoning as the rename path above.
      const liveCount = await getSubjectTutorCount(deleteRow.name);
      if (liveCount > 0) {
        showError(guardMessage(liveCount, "delete"));
        setDeleteRow(null);
        await refetch();
        return;
      }
      await deleteSubject(deleteRow.id);
      await logUserActivity({
        userId,
        userName,
        userEmail,
        action: "DELETE_SUBJECT",
        entityType: "subject",
        entityId: deleteRow.id,
        entityName: deleteRow.name,
        before: {
          name: deleteRow.name,
          order: deleteRow.order,
          icon: deleteRow.icon || "",
        },
        after: {},
        description: `${userName} deleted subject ${deleteRow.name}`,
      });
      setDeleteRow(null);
      await refetch();
    } catch (e) {
      showError(e.message || "Failed to delete subject.");
    } finally {
      setDeleting(false);
    }
  };

  const nameLocked = dialogMode === "edit" && !canMutateName(editingRow?.tutorCount);

  const columns = [
    { key: "name", label: "Name", sortable: true },
    { key: "order", label: "Order", sortable: true },
    { key: "icon", label: "Icon", render: (row) => row.icon || "—" },
    { key: "tutorCount", label: "Tutors", sortable: true },
    {
      key: "actions",
      label: "Actions",
      render: (row) => {
        const locked = !canMutateName(row.tutorCount);
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            {hasPermission(userRole, permissions.EDIT_SUBJECTS) && (
              <IconButton
                size="small"
                color="primary"
                onClick={() => openEdit(row)}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
            {hasPermission(userRole, permissions.DELETE_SUBJECTS) && (
              <Tooltip title={locked ? `${row.tutorCount} tutors carry this name` : ""}>
                <span>
                  <IconButton
                    size="small"
                    color="error"
                    disabled={locked}
                    onClick={() => setDeleteRow(row)}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: "bold", mt: 1 }}>
          Subjects
        </Typography>
      </Box>

      <DataTable
        columns={columns}
        rows={data}
        searchKeys={["name"]}
        initialSort={{ field: "order", direction: "asc" }}
        loading={loading}
        error={error}
        emptyText="No subjects yet."
        headerActions={
          hasPermission(userRole, permissions.EDIT_SUBJECTS) && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAdd}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                px: 2,
                height: "40px",
                boxShadow: "none",
              }}
            >
              Add Subject
            </Button>
          )
        }
      />

      {/* Add/Edit Dialog */}
      <Dialog
        open={Boolean(dialogMode)}
        onClose={closeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          {dialogMode === "add" ? "Add Subject" : "Edit Subject"}
        </DialogTitle>
        <DialogContent dividers>
          <Tooltip title={nameLocked ? `${editingRow.tutorCount} tutors carry this name` : ""}>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              value={formData.name}
              onChange={(e) => handleNameChange(e.target.value)}
              disabled={nameLocked}
              error={Boolean(formErrors.name)}
              helperText={formErrors.name || ""}
              sx={dialogFieldSx}
            />
          </Tooltip>
          <TextField
            label="Subject ID"
            fullWidth
            margin="normal"
            value={formData.id}
            disabled
            helperText="Firestore document id, derived from the name"
            sx={dialogFieldSx}
          />
          <TextField
            type="number"
            label="Order"
            fullWidth
            margin="normal"
            value={formData.order}
            onChange={(e) =>
              setFormData({ ...formData, order: Number(e.target.value) })
            }
            error={Boolean(formErrors.order)}
            helperText={formErrors.order || "Position in the app's subject chip list"}
            sx={dialogFieldSx}
          />
          <TextField
            label="Icon (optional)"
            fullWidth
            margin="normal"
            value={formData.icon}
            onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
            sx={dialogFieldSx}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={saving} sx={actionButtonSx}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            sx={actionButtonSx}
          >
            {saving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog
        open={Boolean(deleteRow)}
        onClose={() => !deleting && setDeleteRow(null)}
        PaperProps={{ sx: { borderRadius: "8px" } }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>Delete Subject</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2">
            Delete "{deleteRow?.name}"? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteRow(null)}
            disabled={deleting}
            sx={actionButtonSx}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            variant="contained"
            color="error"
            disabled={deleting}
            sx={actionButtonSx}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={closeSnackbar}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Subjects;
